import {
  IPubsub_Message_Payload,
  IPubsub_Subscription,
  IPubsub_Subscription_Handler,
  IPubsub_Subscription_Status,
  IPubsub_Message_Type,
  IPubsub_Message_Body,
  IPubsub_Command_Type,
  IPubsub_Command_Body,
  IPubsub_Socket_Writer,
  IPubsub_Client_Options,
} from '../types/pubsub'

import bluebird from 'bluebird'
import WS from 'isomorphic-ws'
import ReconnectingWebSocket, {
  ErrorEvent,
} from 'reconnecting-websocket'

import {
  getLogger,
} from '../utils/log'

import {
  isDebug_Websockets,
} from '../utils/settings'

import {
  PUBSUB_HANDLER_TYPE_READ,
  PUBSUB_HANDLER_TYPE_COMMAND,
  PUBSUB_HANDLER_TYPE_SUBSCRIBE,
  PUBSUB_HANDLER_TYPE_SUBSCRIBE_SUCCESS,
  PUBSUB_HANDLER_TYPE_SUBSCRIBE_ERROR,
  PUBSUB_HANDLER_TYPE_UNSUBSCRIBE,

  PUBSUB_SUBSCRIPTION_STATUS_CONNECTING,
  PUBSUB_SUBSCRIPTION_STATUS_CONNECTED,
  PUBSUB_SUBSCRIPTION_STATUS_ERROR,
  PUBSUB_SUBSCRIPTION_LOOP_DELAY,
  PUBSUB_SUBSCRIPTION_LOOP_TIMEOUT,
} from '../constants'

import {
  getMatchingChannels,
} from './utils'

const logger = getLogger(isDebug_Websockets())

const PubSubClient = ({
  url,
  token,
  maxConnectionAttempts = 10,
  commandHandler,
}: IPubsub_Client_Options) => {

  // the websocket connection
  let socket: any = null
  
  // the list of active subscription handlers
  let subscriptions: Record<string, IPubsub_Subscription<any, any>> = {}

  // how many times have we tried to get an initial connection?
  let connectionAttempts = 0

  const urlProvider = async () => {
    return `${url}?access_token=${token}`
  }

  // return a promise that resolves once the socket is connected
  // this means we can "wait" for the socket to be active
  const connect = async () => {
    return new Promise((resolve, reject) => {
      try {
        logger.log(`attempting websocket connection: ${url}`)

        socket = new ReconnectingWebSocket(urlProvider, [], {
          WebSocket: WS,
          debug: isDebug_Websockets(),
        })
    
        socket.addEventListener('open', () => {
          logger.log('socket client: connected')
          connectionAttempts = 0
          resubscribe()
          resolve(undefined)
        })
        
        socket.addEventListener('close', () => {
          logger.log('socket client: disconnected')
          connectionAttempts++
          if(connectionAttempts >= maxConnectionAttempts) {
            socket.close()
            reject(`websocket failed to connect after ${maxConnectionAttempts} attempts`)
          }
        })
    
        socket.addEventListener('error', (event: ErrorEvent) => {
          logger.error(`socket client: error: ${event.message}`)
        })
      
        socket.addEventListener('message', (event: any) => {
          const message: IPubsub_Message_Payload = JSON.parse(event.data)
          readMessage(message)
        })

      } catch(e) {
        reject(e)
      }
    })
  }

  const readMessage = (message: IPubsub_Message_Payload) => {
    logger.log(`socket client: message`)
    logger.json(message)

    // this is a message destined for a subscription handler
    if(message.handler == PUBSUB_HANDLER_TYPE_READ && message.channel) {
      // the message channel itself is the full channel and we might be subscribed
      // to a wildcard channel - let's get a match
      const matchingChannels = getMatchingChannels(Object.keys(subscriptions), message.channel)
      matchingChannels.forEach(matchingChannel => {
        const subscription = subscriptions[matchingChannel]
        if(!subscription) return
        subscription.handlers.forEach(handler => {
          handler(message)
        })
        Object.keys(subscription.filters).forEach(filter => {
          const handler = subscription.filters[filter]
          handler(message)
        })
      })
    }
    else if(message.handler == PUBSUB_HANDLER_TYPE_SUBSCRIBE_SUCCESS && message.channel) {
      if(subscriptions[message.channel]) {
        const subscription = subscriptions[message.channel]
        subscription.status = PUBSUB_SUBSCRIPTION_STATUS_CONNECTED
      }
    }
    else if(message.handler == PUBSUB_HANDLER_TYPE_SUBSCRIBE_ERROR && message.channel) {
      if(subscriptions[message.channel]) {
        const subscription = subscriptions[message.channel]
        subscription.status = PUBSUB_SUBSCRIPTION_STATUS_ERROR
        subscription.error = (message.body || `error subscribing`) as string
      }
    }
    else if(message.handler == PUBSUB_HANDLER_TYPE_COMMAND && message.messageType && commandHandler) {
      commandHandler(message.messageType as IPubsub_Command_Type, message.body as IPubsub_Command_Body)
    }
  }

  async function resubscribe() {
    Object.keys(subscriptions).forEach(channel => {
      send({
        handler: PUBSUB_HANDLER_TYPE_SUBSCRIBE,
        channel,
      })
    })
  }

  async function subscribe<MessageType = IPubsub_Message_Type, BodyType = IPubsub_Message_Body>(channel: string, handler: IPubsub_Subscription_Handler<MessageType, BodyType>, filter?: string) {

    // allow multiple handlers for the same subscription
    if(subscriptions[channel]) {
      const subscription = subscriptions[channel]
      if(filter) {
        subscription.filters[filter] = handler
      }
      else {
        subscription.handlers.push(handler)
      }
      return
    }

    const subscription: IPubsub_Subscription<MessageType, BodyType> = {
      channel,
      handlers: [],
      filters: {},
      status: PUBSUB_SUBSCRIPTION_STATUS_CONNECTING,
    }

    if(filter) {
      subscription.filters[filter] = handler
    }
    else {
      subscription.handlers.push(handler)
    }

    subscriptions[channel] = subscription

    send({
      handler: PUBSUB_HANDLER_TYPE_SUBSCRIBE,
      channel,
    })

    // now we wait for the state of the subscription
    // this will be updated above in readMessage
    // a PUBSUB_HANDLER_TYPE_SUBSCRIBE_{SUCCESS,ERROR} message
    // will arrive for this channel
    // include a timeout in case we don't get that message back
    let currentStatus: IPubsub_Subscription_Status = PUBSUB_SUBSCRIPTION_STATUS_CONNECTING
    let loops = 0
    const maxLoops = PUBSUB_SUBSCRIPTION_LOOP_TIMEOUT / PUBSUB_SUBSCRIPTION_LOOP_DELAY

    while(currentStatus == PUBSUB_SUBSCRIPTION_STATUS_CONNECTING && loops < maxLoops) {
      await bluebird.delay(PUBSUB_SUBSCRIPTION_LOOP_DELAY)
      currentStatus = subscription.status
    }

    // we must have timed out trying to get a confirmation
    if(currentStatus == PUBSUB_SUBSCRIPTION_STATUS_CONNECTING) {
      throw new Error(`did not get subscription within ${PUBSUB_SUBSCRIPTION_LOOP_TIMEOUT} ms`)
    }
    else if(currentStatus == PUBSUB_SUBSCRIPTION_STATUS_ERROR) {
      throw new Error(`subscription error: ${subscription.error}`)
    }
  }

  const unsubscribe = (channel: string, filter?: string) => {
    if(!subscriptions[channel]) return
    let removeSubscription = false
    if(!filter) {
      removeSubscription = true
    }
    else {
      const sub = subscriptions[channel]
      delete(sub.filters[filter])

      if(sub.handlers.length <= 0 && Object.keys(sub.filters).length <= 0) removeSubscription = true
    }
    if(removeSubscription) {
      delete(subscriptions[channel])
      send({
        handler: PUBSUB_HANDLER_TYPE_UNSUBSCRIBE,
        channel,
      })
    }
  }

  const send: IPubsub_Socket_Writer = (message) => {
    if(!socket) throw new Error(`no socket found`)
    socket.send(JSON.stringify(message))
  }

  const close = () => {
    if(!socket) throw new Error(`no socket found`)
    socket.close()
  }

  return {
    connect,
    subscribe,
    unsubscribe,
    send,
    close,
  }
}

export default PubSubClient